import React, { useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Code2, Globe, Settings, Palette, CheckCircle, Star, Users, FolderOpen, Award } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import './PublicPages.css';

const services = [
  { icon: Code2, title: 'Software Development', desc: 'Custom enterprise software built with modern technologies, scalable architecture, and best practices.' },
  { icon: Globe, title: 'Web Development', desc: 'Stunning, high-performance websites and web applications that deliver exceptional user experiences.' },
  { icon: Settings, title: 'System Maintenance', desc: 'Ongoing technical support, updates, and maintenance to keep your systems running at peak performance.' },
  { icon: Palette, title: 'Graphic Design', desc: 'Professional brand identity, UI/UX design, and creative assets that make your business stand out.' },
];

const stats = [
  { value: '50+', label: 'Projects Delivered', icon: FolderOpen },
  { value: '20+', label: 'Happy Clients', icon: Users },
  { value: '98%', label: 'Client Satisfaction', icon: Star },
  { value: '5+', label: 'Years Experience', icon: Award },
];

const testimonials = [
  { name: 'John Smith', company: 'RetailZA Ltd', text: 'Fortitude IT completely transformed our e-commerce operations. Their team delivered beyond expectations.', rating: 5 },
  { name: 'Sarah Khumalo', company: 'FinBank Solutions', text: 'The banking API they built is rock-solid. Security, performance, and reliability — they nailed it.', rating: 5 },
  { name: 'Mike Peters', company: 'FitLife Wellness', text: 'Professional, skilled, and responsive. Our mobile app has 50k downloads thanks to their work.', rating: 5 },
];

function newsSortKey(item) {
  const raw = item?.date;
  if (!raw) return 0;
  const t = Date.parse(raw);
  return Number.isNaN(t) ? 0 : t;
}

export default function HomePage() {
  const { data } = useApp();
  const { pathname, hash } = useLocation();

  const publishedNews = useMemo(() => {
    const list = data.websiteNews || [];
    return list
      .filter((n) => n.published === true || n.published === undefined)
      .sort((a, b) => newsSortKey(b) - newsSortKey(a));
  }, [data.websiteNews]);

  useEffect(() => {
    if (pathname === '/' && hash === '#fortitude-news') {
      const el = document.getElementById('fortitude-news');
      queueMicrotask(() => el?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  }, [pathname, hash]);

  return (
    <div className="home-page">
      {/* Hero */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-orb hero-orb-1" />
          <div className="hero-orb hero-orb-2" />
          <div className="hero-grid" />
        </div>
        <div className="hero-content">
          <div className="hero-badge">
            <span className="hero-badge-dot" />
            South Africa's Premier IT Solutions Provider
          </div>
          <h1 className="hero-title">
            We Build
            <span className="hero-title-gold"> Digital Solutions</span>
            <br />
            That Drive Growth
          </h1>
          <p className="hero-subtitle">
            Fortitude IT Solutions delivers world-class software development, web development, system maintenance, and graphic design services across Africa and beyond.
          </p>
          <div className="hero-actions">
            <Link to="/portfolio" className="btn-gold hero-btn">
              View Our Work <ArrowRight size={16} />
            </Link>
            <Link to="/contact" className="btn-outline hero-btn">
              Get a Quote
            </Link>
          </div>
          <div className="hero-checks">
            {['Enterprise-Grade Solutions', 'Agile Development', '24/7 Support'].map((c) => (
              <div key={c} className="hero-check">
                <CheckCircle size={14} style={{ color: 'var(--secondary-color)' }} />
                <span>{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="stats-band">
        <div className="section-container">
          <div className="stats-band-grid">
            {stats.map((s) => (
              <div key={s.label} className="stats-band-item">
                <s.icon size={20} style={{ color: 'var(--secondary-color)' }} />
                <span className="stats-band-value">{s.value}</span>
                <span className="stats-band-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="services-section">
        <div className="section-container">
          <div className="section-tag">What We Do</div>
          <h2 className="section-heading">Our Core Services</h2>
          <p className="section-subheading">From concept to deployment, we handle every aspect of your digital journey with precision and expertise.</p>
          <div className="services-grid">
            {services.map((s) => (
              <div key={s.title} className="service-card">
                <div className="service-icon">
                  <s.icon size={24} />
                </div>
                <h3 className="service-title">{s.title}</h3>
                <p className="service-desc">{s.desc}</p>
                <Link to="/services" className="service-link">
                  Learn more <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* News */}
      <section id="fortitude-news" className="news-section">
        <div className="section-container">
          <div className="section-tag">News &amp; updates</div>
          <h2 className="section-heading">From the Fortitude team</h2>
          <p className="section-subheading">Company milestones, partnerships, and announcements — published from your Website admin in the portal.</p>
          {publishedNews.length === 0 ? (
            <p className="news-empty">No published news yet. Check back soon.</p>
          ) : (
            <div className="news-grid">
              {publishedNews.map((item) => (
                <article key={item.id} className="news-card">
                  <p className="news-date">{item.date ? new Date(item.date + 'T12:00:00').toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}</p>
                  <h3 className="news-title">{item.title}</h3>
                  <p className="news-excerpt">{item.excerpt || item.summary || ''}</p>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Banner */}
      <section className="cta-banner">
        <div className="section-container">
          <div className="cta-inner">
            <div className="cta-bg" />
            <div className="cta-content">
              <h2 className="cta-title">Ready to Build Something Great?</h2>
              <p className="cta-subtitle">Let's discuss your project and create a solution tailored to your business needs.</p>
              <div style={{ display: 'flex', gap: 14 }}>
                <Link to="/contact" className="btn-gold">
                  Start a Project <ArrowRight size={14} />
                </Link>
                <Link to="/portfolio" className="btn-outline" style={{ background: 'transparent' }}>
                  View Portfolio
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="section-container">
          <div className="section-tag">Client Reviews</div>
          <h2 className="section-heading">What Our Clients Say</h2>
          <div className="testimonials-grid">
            {testimonials.map((t) => (
              <div key={t.name} className="testimonial-card">
                <div className="testimonial-stars">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} size={14} fill="var(--secondary-color)" color="var(--secondary-color)" />
                  ))}
                </div>
                <p className="testimonial-text">"{t.text}"</p>
                <div className="testimonial-author">
                  <div className="avatar avatar-gold" style={{ width: 36, height: 36, fontSize: '0.8rem' }}>
                    {t.name.split(' ').map((n) => n[0]).join('')}
                  </div>
                  <div>
                    <p className="testimonial-name">{t.name}</p>
                    <p className="testimonial-company">{t.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
